/**
 * What PostHog is allowed to learn about the address a person is on.
 *
 * Every event PostHog records carries the page's full address ($current_url, $referrer, the first address the person ever
 * landed on, the address of a link they clicked, a heatmap keyed by address). In this app an address can carry a secret:
 *
 *   /claim/<token>, /admin-claim/<token>, /billing/delegate/<token>   the token IS the way in
 *   ?email=...  ?claim=...  ?adminClaim=...  ?session_id=cs_...       an e-mail, a claim token, a Stripe session
 *   #session_id=...                                                    the one-time sign-in return from Google
 *
 * (A mailto:, tel: or sms: link is cut to its scheme: that it was one, not whose.)
 *
 * So before an event leaves the browser (PostHog's `before_send`), every address in it, at any depth, is cut down to where the
 * person was, not what they were carrying: no query, no fragment, and the secret part of the path replaced by ":token". The page is still
 * known ("/claim/:token", "/plan", "/communities/<id>/admin"), which is all the funnel needs. The utm_* properties PostHog works
 * out from the address are separate properties made before this runs, so campaigns are still told apart.
 *
 * When in doubt the event is dropped rather than sent. Pure JS, so `node --test` can run it (analyticsScrub.test.mjs).
 */

/** A path segment that follows one of these is a secret (App.js: /claim/:token, /admin-claim/:token, /billing/delegate/:token). */
export const TOKEN_AFTER = Object.freeze(['claim', 'admin-claim', 'delegate']);

// Event properties that hold an address (a web address, or just its path), at any depth: $current_url, attr__href, a src...
const ADDRESS_KEY = /url|href|src|referrer|pathname/i;
// A string that is, all of it, a web address, whatever it is called: a resource's `name`, an `attribution.url`, a link target.
const WHOLE_ADDRESS = /^(?:[a-z][a-z0-9+.-]*:)?\/\/\S+$/i;
// Deeper than any event PostHog builds (web vitals, the deepest, go about five levels): past this the event is not sent.
const MAX_DEPTH = 12;

function maskPath(path) {
  const parts = path.split('/');
  for (let i = 1; i < parts.length; i += 1) {
    if (parts[i] && TOKEN_AFTER.includes(parts[i - 1].toLowerCase())) parts[i] = ':token';
  }
  return parts.join('/');
}

/** An address with no query, no fragment and no secret path segment. Anything that is not text is returned as it is. */
export function safeAddress(value) {
  if (typeof value !== 'string' || !value) return value;
  // A link that is a person (mailto:, tel:, sms:) is a link to somebody: keep that it was one, not who.
  const person = value.match(/^(mailto|tel|sms):/i);
  if (person) return `${person[1].toLowerCase()}:`;
  const cut = value.search(/[?#]/);
  const bare = cut === -1 ? value : value.slice(0, cut);
  const origin = (bare.match(/^(?:[a-z][a-z0-9+.-]*:)?\/\/[^/]*/i) || [''])[0];
  return origin + maskPath(bare.slice(origin.length));
}

/**
 * `value` with every address in it cut down, at any depth: PostHog's web-vitals events keep the page's address inside each metric
 * (properties.$web_vitals_FCP_event.$current_url) and inside the browser's own timing entries, and nothing says where else it
 * will turn up. A browser object (a timing entry) is read as the plain object it becomes when sent (toJSON), so its fields
 * are seen too. Throws past MAX_DEPTH, and scrubEvent then sends nothing.
 */
function scrubValue(key, value, depth, seen) {
  if (typeof value === 'string') {
    if (key === '$elements_chain') {
      // autocapture's record of what was clicked: ...href="/claim/abc?x=1"...
      return value.replace(/href="([^"]*)"/g, (_, href) => `href="${safeAddress(href)}"`);
    }
    return ADDRESS_KEY.test(key) || WHOLE_ADDRESS.test(value) ? safeAddress(value) : value;
  }
  if (!value || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH) throw new Error('too deep to check');
  if (typeof value.toJSON === 'function') {
    const plain = value.toJSON();
    if (!plain || typeof plain !== 'object') return plain;
    value = plain;
  }
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    value.forEach((item, i) => { value[i] = scrubValue(key, item, depth + 1, seen); });
    return value;
  }
  if (key === '$heatmap_data') {
    // heatmap clicks, keyed by the address they were made on
    const byAddress = {};
    for (const [address, clicks] of Object.entries(value)) byAddress[safeAddress(address)] = scrubValue(address, clicks, depth + 1, seen);
    return byAddress;
  }
  for (const name of Object.keys(value)) value[name] = scrubValue(name, value[name], depth + 1, seen);
  return value;
}

/** PostHog's `before_send`: the event with every address cut down, or null (send nothing) if it could not be made safe. */
export function scrubEvent(event) {
  try {
    if (!event || typeof event !== 'object') return event;
    const seen = new WeakSet();
    for (const part of ['properties', '$set', '$set_once']) {
      if (event[part] && typeof event[part] === 'object') event[part] = scrubValue(part, event[part], 0, seen);
    }
    return event;
  } catch (_) {
    return null;
  }
}
